import Dexie, { type Table } from 'dexie'
import { compatiblePayloads } from './payloadHash'
import type { EventRow, JSONValue, WriterRole, EventType, EventSource } from './types'

/**
 * Local IndexedDB mirror of the server (Person Graph).
 * Offline-first: every UI write goes into `events` and enqueues in `outbox`.
 * Outbox drains async to the server via SyncEngine.
 * Shadow tables (persons_shadow, needs_shadow, early_care_state_shadow) are populated
 * by Realtime subscribers so offline reads work.
 */

export interface LocalEvent {
  id: string
  family_id: string
  person_id: string | null
  user_id: string
  device_id: string
  writer_role: WriterRole
  event_type: EventType
  payload_json: string
  payload_hash: string
  schema_version: number
  occurred_at: string
  recorded_at: string
  server_received_at: string | null
  server_sequence: number | null
  supersedes_event_id: string | null
  source: EventSource
}

export interface OutboxEntry {
  event_id: string
  state: 'pending' | 'in_flight' | 'synced' | 'failed'
  attempts: number
  last_attempt_at: string | null
  last_error: string | null
  next_retry_at: string
}

export interface FamilyCursor {
  family_id: string
  last_catchup_sequence: number
  last_catchup_at: string
}

export interface PersonShadow {
  id: string
  family_id: string
  json: string      // full Person row serialized
  updated_at: string
}

export interface NeedShadow {
  id: string
  family_id: string
  person_id: string | null
  status: string
  json: string
  updated_at: string
}

export interface EarlyCareStateShadow {
  person_id: string
  family_id: string
  json: string
  updated_at: string
}

class BabyOSDB extends Dexie {
  events!: Table<LocalEvent, string>
  outbox!: Table<OutboxEntry, string>
  family_cursor!: Table<FamilyCursor, string> // legacy, never trusted by protocol v2
  durable_cursors_v2!: Table<FamilyCursor, string>
  persons_shadow!: Table<PersonShadow, string>
  needs_shadow!: Table<NeedShadow, string>
  early_care_state_shadow!: Table<EarlyCareStateShadow, string>

  constructor() {
    super('BabyOSLocal')
    this.version(1).stores({
      events:
        'id, family_id, person_id, [family_id+server_sequence], [person_id+occurred_at], [family_id+event_type+occurred_at], server_sequence',
      outbox: 'event_id, state, next_retry_at, [state+next_retry_at]',
      family_cursor: 'family_id',
      persons_shadow: 'id, family_id',
      needs_shadow: 'id, family_id, person_id, [family_id+status]',
      early_care_state_shadow: 'person_id, family_id',
    })
    // Do NOT copy family_cursor: A8, Realtime and skipped local writes could
    // already have poisoned it. Replay from zero into the existing event cache.
    // Keep the old table isolated so old tabs cannot advance the v2 checkpoint.
    // Pending events/outbox rows are untouched by this schema upgrade.
    this.version(2).stores({ durable_cursors_v2: 'family_id' })
  }
}

export const db = new BabyOSDB()

/**
 * Append an event locally and enqueue in outbox. Idempotent by event.id.
 * Hash collision (same id, different payload) throws — this is a bug/attack signal.
 */
export async function appendLocalEvent(evt: {
  id: string
  family_id: string
  person_id: string | null
  user_id: string
  device_id: string
  writer_role: WriterRole
  event_type: EventType
  payload: JSONValue
  payload_hash: string
  schema_version: number
  occurred_at: string
  recorded_at: string
  supersedes_event_id?: string | null
  source?: EventSource
}): Promise<void> {
  await db.transaction('rw', db.events, db.outbox, async () => {
    const existing = await db.events.get(evt.id)
    if (existing) {
      if (existing.payload_hash !== evt.payload_hash) {
        throw new Error(
          `hash_collision id=${evt.id} existing=${existing.payload_hash} new=${evt.payload_hash}`,
        )
      }
      return // idempotent no-op
    }
    await db.events.put({
      id: evt.id,
      family_id: evt.family_id,
      person_id: evt.person_id,
      user_id: evt.user_id,
      device_id: evt.device_id,
      writer_role: evt.writer_role,
      event_type: evt.event_type,
      payload_json: JSON.stringify(evt.payload),
      payload_hash: evt.payload_hash,
      schema_version: evt.schema_version,
      occurred_at: evt.occurred_at,
      recorded_at: evt.recorded_at,
      server_received_at: null,
      server_sequence: null,
      supersedes_event_id: evt.supersedes_event_id ?? null,
      source: evt.source ?? 'app',
    })
    await db.outbox.put({
      event_id: evt.id,
      state: 'pending',
      attempts: 0,
      last_attempt_at: null,
      last_error: null,
      next_retry_at: new Date().toISOString(),
    })
  })
}

/** Mark a local event as synced after the server ACKs it. */
export async function markSynced(id: string, serverSequence: number, serverReceivedAt: string): Promise<void> {
  await db.transaction('rw', db.events, db.outbox, async () => {
    const existing = await db.events.get(id)
    if (!existing) return
    await db.events.update(id, {
      server_sequence: serverSequence,
      server_received_at: serverReceivedAt,
    })
    await db.outbox.update(id, { state: 'synced' })
  })
}

/** Absorb a server-side event that came in via Realtime OR catch_up_events. */
export async function absorbRemoteEvent(row: EventRow): Promise<void> {
  await db.transaction('rw', db.events, db.outbox, async () => {
    const existing = await db.events.get(row.id)
    const payloadJson = JSON.stringify(row.payload)
    if (existing) {
      // Payload content is authoritative across legacy/canonical hash encodings.
      // Check even equal hashes: a digest cannot excuse a different payload/tenant.
      // waitFor keeps the IDB transaction alive during WebCrypto (including pages).
      if (existing.family_id !== row.family_id || !await Dexie.waitFor(compatiblePayloads(
        existing.payload_json, row.payload, existing.payload_hash, row.payload_hash,
      ))) {
        throw new Error(
          `hash_collision id=${row.id} local=${existing.payload_hash} remote=${row.payload_hash}`,
        )
      }
      // Advance server metadata even if we already had it locally
      if (row.server_sequence != null && (existing.server_sequence == null || row.server_sequence > existing.server_sequence)) {
        await db.events.update(row.id, {
          server_sequence: row.server_sequence,
          server_received_at: row.server_received_at,
        })
        await db.outbox.update(row.id, { state: 'synced' })
      }
      return
    }
    await db.events.put({
      id: row.id,
      family_id: row.family_id,
      person_id: row.person_id,
      user_id: row.user_id,
      device_id: row.device_id,
      writer_role: row.writer_role,
      event_type: row.event_type,
      payload_json: payloadJson,
      payload_hash: row.payload_hash,
      schema_version: row.schema_version,
      occurred_at: row.occurred_at,
      recorded_at: row.recorded_at,
      server_received_at: row.server_received_at,
      server_sequence: row.server_sequence,
      supersedes_event_id: row.supersedes_event_id,
      source: row.source,
    })
    // Remote events are already synced — no outbox entry needed
  })
}

export async function readCursor(familyId: string): Promise<number> {
  const c = await db.durable_cursors_v2.get(familyId)
  return c?.last_catchup_sequence ?? 0
}

/**
 * The ONLY durable cursor writer. Call only with a complete, ordered v2 RPC page.
 * A hash collision, malformed page, quota error or interrupted transaction must
 * leave BOTH the page and checkpoint uncommitted. Realtime/ACKs never call this.
 */
export async function absorbCatchUpPage(familyId: string, since: number, rows: EventRow[]): Promise<void> {
  if (!Number.isSafeInteger(since) || since < 0) throw new Error('invalid_catchup_cursor')
  let next = since
  for (const row of rows) {
    const seq = row.server_sequence
    if (row.family_id !== familyId || seq == null || !Number.isSafeInteger(seq) || seq <= next) {
      throw new Error('invalid_catchup_page')
    }
    next = seq
  }
  if (rows.length === 0) return
  await db.transaction('rw', db.events, db.outbox, db.durable_cursors_v2, async () => {
    const current = await readCursor(familyId)
    if (current < since) throw new Error('catchup_cursor_ahead_of_local_history')
    for (const row of rows) await absorbRemoteEvent(row)
    await db.durable_cursors_v2.put({
      family_id: familyId,
      last_catchup_sequence: Math.max(current, next),
      last_catchup_at: new Date().toISOString(),
    })
  })
}

/** Recent events for a Person, excluding superseded. */
export async function recentEventsForPerson(personId: string, limit = 100): Promise<LocalEvent[]> {
  const supersededIds = new Set(
    (await db.events.filter((e) => e.supersedes_event_id != null).toArray())
      .map((e) => e.supersedes_event_id!)
      .filter(Boolean),
  )
  const rows = await db.events
    .where('[person_id+occurred_at]')
    .between([personId, Dexie.minKey], [personId, Dexie.maxKey])
    .reverse()
    .limit(limit + supersededIds.size)
    .toArray()
  return rows.filter((r) => !supersededIds.has(r.id)).slice(0, limit)
}

// No schema migration: last_attempt_at doubles as a lease/fencing token.
export const OUTBOX_LEASE_MS = 60_000
const BACKOFF_SEC = [2, 5, 15, 60, 300, 900, 3600]

function outboxEligible(entry: OutboxEntry, now: number): boolean {
  if (entry.state === 'synced') return false
  if (entry.state === 'in_flight') {
    const started = Date.parse(entry.last_attempt_at ?? '')
    return !Number.isFinite(started) || started + OUTBOX_LEASE_MS <= now
  }
  const retry = Date.parse(entry.next_retry_at)
  return !Number.isFinite(retry) || retry <= now
}

/** Pending, expired leases and exhausted failures all progress automatically.
 * Oldest due work first; failed remains visible between hourly retries.
 */
export async function pendingOutbox(limit = 20, userId?: string): Promise<LocalEvent[]> {
  const now = Date.now()
  const outbox = await db.outbox.where('state').anyOf('pending', 'in_flight', 'failed')
    .and((o) => outboxEligible(o, now)).sortBy('next_retry_at')
  const events = await db.events.bulkGet(outbox.map((o) => o.event_id))
  return events.filter((e): e is LocalEvent => e != null && (userId === undefined || e.user_id === userId)).slice(0, limit)
}

/** Earliest wake-up for the signed-in author's queue; orphan rows cannot spin. */
export async function nextOutboxAttemptAt(userId: string): Promise<number | null> {
  const entries = await db.outbox.where('state').anyOf('pending', 'in_flight', 'failed').toArray()
  const events = await db.events.bulkGet(entries.map((o) => o.event_id))
  const times = entries.flatMap((entry, i) => {
    if (events[i]?.user_id !== userId) return []
    const at = entry.state === 'in_flight'
      ? Date.parse(entry.last_attempt_at ?? '') + OUTBOX_LEASE_MS : Date.parse(entry.next_retry_at)
    return [Number.isFinite(at) ? at : Date.now()]
  })
  return times.length ? times.reduce((a, b) => Math.min(a, b)) : null
}

/** Atomic cross-tab claim. A selection is NOT a claim. */
export async function markInFlight(id: string): Promise<string | null> {
  return db.transaction('rw', db.outbox, async () => {
    const entry = await db.outbox.get(id)
    if (!entry || !outboxEligible(entry, Date.now())) return null
    const lease = new Date(Math.max(Date.now(), Date.parse(entry.last_attempt_at ?? '') + 1 || 0)).toISOString()
    await db.outbox.update(id, { state: 'in_flight', last_attempt_at: lease })
    return lease
  })
}

export async function recordOutboxFailure(id: string, error: string, lease?: string): Promise<void> {
  await db.transaction('rw', db.outbox, async () => {
    const entry = await db.outbox.get(id)
    // A late failed request must never undo an ACK or a newer tab's claim.
    if (!entry || entry.state === 'synced' || (lease !== undefined && entry.last_attempt_at !== lease)) return
    const attempts = entry.attempts + 1
    const idx = Math.min(attempts - 1, BACKOFF_SEC.length - 1)
    await db.outbox.update(id, {
      attempts, last_error: error,
      state: attempts > BACKOFF_SEC.length ? 'failed' : 'pending',
      next_retry_at: new Date(Date.now() + BACKOFF_SEC[idx] * 1000).toISOString(),
    })
  })
}

/**
 * Write a shadow row from server projections/tables (Realtime subscribers call this).
 * `type` is the shadow table name.
 */
export async function writePersonShadow(person: import('./types').Person): Promise<void> {
  await db.persons_shadow.put({
    id: person.id,
    family_id: person.family_id,
    json: JSON.stringify(person),
    updated_at: new Date().toISOString(),
  })
}

export async function writeNeedShadow(need: import('./types').Need): Promise<void> {
  await db.needs_shadow.put({
    id: need.id,
    family_id: need.family_id,
    person_id: need.person_id,
    status: need.status,
    json: JSON.stringify(need),
    updated_at: new Date().toISOString(),
  })
}

export async function writeEarlyCareShadow(ec: import('./types').EarlyCareState): Promise<void> {
  await db.early_care_state_shadow.put({
    person_id: ec.person_id,
    family_id: ec.family_id,
    json: JSON.stringify(ec),
    updated_at: new Date().toISOString(),
  })
}
